(() => {
  'use strict';
  const root = document.documentElement;
  const reduced = window.matchMedia('(prefers-reduced-motion:reduce)');
  const economical = navigator.connection?.saveData || navigator.hardwareConcurrency <= 2 || navigator.deviceMemory <= 2;
  const sceneMedia = window.matchMedia('(min-width:750px)');
  const selectors = '[data-p-reveal],.px-page-heading,.px-product__gallery,.px-product__info,.px-related,.px-cart-summary,.px-cart-line,.dy-page-heading,.dy-memory-page__hero,.dy-community__hero,.dy-contact__form-panel,.px-catalog-content .px-card';
  const scenes = new Map();
  const activeScenes = new Set();
  const animations = new Set();
  let reveals = new WeakSet();
  let revealObserver;
  let sceneObserver;
  let frame = 0;
  let resizeTimer = 0;
  let enabled = false;
  let viewportHeight = window.innerHeight;
  let scrollTop = window.scrollY;
  const available = () => !reduced.matches && !economical && typeof Element.prototype.animate === 'function' && 'IntersectionObserver' in window;
  const reveal = element => {
    revealObserver?.unobserve(element);
    if (!enabled || document.visibilityState === 'hidden' || !element.isConnected) return;
    const animation = element.animate([
      { opacity: .68, transform: 'translate3d(0,12px,0)' },
      { opacity: 1, transform: 'translate3d(0,0,0)' }
    ], { duration: 620, easing: 'cubic-bezier(.22,.61,.36,1)' });
    animations.add(animation);
    animation.finished.then(() => animations.delete(animation), () => animations.delete(animation));
  };
  const measure = () => {
    viewportHeight = window.innerHeight;
    scrollTop = window.scrollY;
    scenes.forEach((scene, element) => {
      const box = element.getBoundingClientRect();
      scene.top = box.top + scrollTop;
      scene.height = box.height;
    });
  };
  const render = () => {
    frame = 0;
    if (document.visibilityState === 'hidden') return;
    root.classList.toggle('p-is-scrolled', scrollTop > 18);
    if (!enabled || !sceneMedia.matches) return;
    activeScenes.forEach(element => {
      const scene = scenes.get(element);
      if (!scene) return;
      const progress = Math.max(0, Math.min(1, (scrollTop + viewportHeight - scene.top) / (viewportHeight + scene.height)));
      element.style.setProperty('--p-scene-scale', (1.035 - progress * .035).toFixed(4));
      element.style.setProperty('--p-scene-shift', `${((progress - .5) * 18).toFixed(2)}px`);
    });
  };
  const queue = () => {
    if (!frame && document.visibilityState !== 'hidden') frame = requestAnimationFrame(render);
  };
  const collect = (scope = document) => {
    if (!enabled) return;
    scope.querySelectorAll(selectors).forEach(element => {
      if (reveals.has(element) || element.closest('dialog,[data-p-scene-media]') || !element.getClientRects().length) return;
      if (!element.hasAttribute('data-p-reveal') && element.parentElement?.closest('[data-p-reveal]')) return;
      reveals.add(element);
      const box = element.getBoundingClientRect();
      if (box.top < viewportHeight && box.bottom > 0) reveal(element);
      else if (box.top >= viewportHeight) revealObserver.observe(element);
    });
    scope.querySelectorAll('[data-p-scene]').forEach(element => {
      if (scenes.has(element)) return;
      scenes.set(element, { top: 0, height: 0 });
      sceneObserver.observe(element);
    });
    measure();
    queue();
  };
  const stop = () => {
    enabled = false;
    revealObserver?.disconnect();
    sceneObserver?.disconnect();
    animations.forEach(animation => animation.cancel());
    animations.clear();
    scenes.forEach((scene, element) => {
      element.style.removeProperty('--p-scene-scale');
      element.style.removeProperty('--p-scene-shift');
    });
    scenes.clear();
    activeScenes.clear();
    root.classList.remove('p-motion-ready');
  };
  const start = () => {
    stop();
    if (!available()) return;
    enabled = true;
    reveals = new WeakSet();
    root.classList.add('p-motion-ready');
    revealObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => { if (entry.isIntersecting) reveal(entry.target); });
    }, { rootMargin: '0px 0px -24px 0px', threshold: .04 });
    sceneObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) activeScenes.add(entry.target);
        else activeScenes.delete(entry.target);
      });
      queue();
    }, { rootMargin: '60px 0px' });
    collect();
  };
  window.addEventListener('scroll', () => { scrollTop = window.scrollY; queue(); }, { passive: true });
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => { measure(); queue(); }, 100);
  }, { passive: true });
  window.addEventListener('load', () => { measure(); queue(); }, { once: true });
  window.addEventListener('pageshow', event => {
    if (event.persisted) {
      animations.forEach(animation => animation.cancel());
      animations.clear();
      measure();
      queue();
    }
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      cancelAnimationFrame(frame);
      frame = 0;
      animations.forEach(animation => animation.cancel());
      animations.clear();
    } else { measure(); queue(); }
  });
  document.addEventListener('focusin', event => {
    const target = event.target.closest?.('[data-p-reveal]');
    if (target) revealObserver?.unobserve(target);
  });
  document.addEventListener('shopify:section:load', event => collect(event.target));
  reduced.addEventListener('change', start);
  sceneMedia.addEventListener('change', () => {
    if (!sceneMedia.matches) scenes.forEach((scene, element) => {
      element.style.removeProperty('--p-scene-scale');
      element.style.removeProperty('--p-scene-shift');
    });
    measure();
    queue();
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
  queue();
})();
